import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { dropdownVariants } from '../../utils/animations';
import { SkeletonStatCard, SkeletonListItem, Drawer, Toggle } from '../ui';
import EmptyState from '../ui/EmptyState';
import PlanUpsell from '../ui/PlanUpsell';
import { MARKS } from '../../lib/marks';
import BookingDetailDrawer from './BookingDetailDrawer';
import { bookingService } from '../../services/bookingService';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { useWebsite } from '../../contexts/WebsiteContext';
import { Eyebrow, Field, H1, HairlineGrid, Pill, Rule, Stat } from '../ui/kit';

// Icons
const CalendarIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

const XMarkIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ArrowPathIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const ListBulletIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);

const CalendarDaysIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
  </svg>
);

const Cog6ToothIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.212-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const ViewColumnIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125Z" />
  </svg>
);

// Types
interface Booking {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  serviceType: string;
  date: string;
  time: string;
  duration: number; // in minutes
  status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
  notes?: string;
  createdAt: string;
  websiteId?: string;
}

interface BookingSettings {
  enabled: boolean;
  availableHours: {
    start: string;
    end: string;
  };
  availableDays: string[];
  bufferTime: number; // minutes between appointments
}

// Status configuration
const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  confirmed: { label: 'Confirmed', color: 'text-green-700', bgColor: 'bg-green-100' },
  pending: { label: 'Pending', color: 'text-zinc-800', bgColor: 'bg-zinc-200' },
  completed: { label: 'Completed', color: 'text-zinc-700', bgColor: 'bg-zinc-100' },
  cancelled: { label: 'Cancelled', color: 'text-red-700', bgColor: 'bg-red-100' },
};

// Helper functions
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const getRelativeDateLabel = (dateString: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);

  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
  return formatDate(dateString);
};

// Skeleton loading component
const BookingsPageSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header Skeleton */}
    <div>
      <div className="h-8 bg-zinc-200 rounded-lg w-48 animate-pulse" />
      <div className="h-4 bg-zinc-200 rounded w-72 mt-2 animate-pulse" />
    </div>

    {/* Stats Skeleton */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <SkeletonStatCard key={i} />
      ))}
    </div>

    {/* View Toggle Skeleton */}
    <div className="flex gap-2">
      <div className="h-10 bg-zinc-200 rounded-full w-24 animate-pulse" />
      <div className="h-10 bg-zinc-200 rounded-full w-24 animate-pulse" />
    </div>

    {/* List Skeleton */}
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="divide-y divide-zinc-100">
        {[...Array(5)].map((_, i) => (
          <SkeletonListItem key={i} />
        ))}
      </div>
    </div>
  </div>
);

const BookingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { features } = usePlanFeatures();
  const { selectedWebsite } = useWebsite();
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'board'>('list');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [settings, setSettings] = useState<BookingSettings>({
    enabled: false,
    availableHours: { start: '09:00', end: '17:00' },
    availableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    bufferTime: 15,
  });
  // Booking is off AND the plan can't turn it on — the upsell owns the screen,
  // so the list below suppresses its own empty state.
  const showBookingsUpsell = !settings.enabled && !features.bookingsEnable;
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showRescheduleDrawer, setShowRescheduleDrawer] = useState(false);

  // Booking detail drawer state
  const [selectedBookingForDetail, setSelectedBookingForDetail] = useState<Booking | null>(null);
  const [isBookingDetailDrawerOpen, setIsBookingDetailDrawerOpen] = useState(false);

  useEffect(() => {
    if (selectedWebsite?.bookingEnabled) {
      setSettings((current) => ({ ...current, enabled: true }));
    }
  }, [selectedWebsite?.bookingEnabled]);

  // Fetch bookings from API
  const { data, isLoading } = useQuery({
    queryKey: ['bookings', filterStatus],
    queryFn: () => bookingService.getBookings({ status: filterStatus || undefined }),
    placeholderData: (prev) => prev,
  });

  const bookings = data?.bookings || [];

  // Update booking mutation
  const updateBookingMutation = useMutation({
    mutationFn: ({ id, ...fields }: { id: string } & Partial<Booking>) => bookingService.updateBooking(id, fields),
    onMutate: async ({ id, ...fields }) => {
      await queryClient.cancelQueries({ queryKey: ['bookings'] });
      const previous = queryClient.getQueryData(['bookings', filterStatus]);
      queryClient.setQueryData(['bookings', filterStatus], (old: { bookings: Booking[]; total: number } | undefined) => {
        if (!old) return old;
        return { ...old, bookings: old.bookings.map(b => b.id === id ? { ...b, ...fields } : b) };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['bookings', filterStatus], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  // Delete booking mutation
  const deleteBookingMutation = useMutation({
    mutationFn: (id: string) => bookingService.deleteBooking(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['bookings'] });
      const previous = queryClient.getQueryData(['bookings', filterStatus]);
      queryClient.setQueryData(['bookings', filterStatus], (old: { bookings: Booking[]; total: number } | undefined) => {
        if (!old) return old;
        return { ...old, bookings: old.bookings.filter(b => b.id !== id), total: old.total - 1 };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['bookings', filterStatus], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  const handleDeleteBooking = (id: string) => {
    deleteBookingMutation.mutate(id);
    setIsBookingDetailDrawerOpen(false);
    setSelectedBookingForDetail(null);
  };

  // Debounced save for booking detail drawer
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedBookingSave = useCallback((booking: Booking) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      updateBookingMutation.mutate({
        id: booking.id,
        customerName: booking.customerName,
        customerPhone: booking.customerPhone,
        customerEmail: booking.customerEmail,
        serviceType: booking.serviceType,
        date: booking.date,
        time: booking.time,
        duration: booking.duration,
        status: booking.status,
        notes: booking.notes,
      });
    }, 500);
  }, [updateBookingMutation]);

  // Handle booking update
  const handleBookingUpdate = (updatedBooking: Booking) => {
    setSelectedBookingForDetail(updatedBooking);
    debouncedBookingSave(updatedBooking);
  };

  const filteredBookings = bookings;

  // Sort by date (upcoming first)
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateA.getTime() - dateB.getTime();
  });

  // Stats
  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };

  // Actions
  const handleConfirm = (bookingId: string) => {
    updateBookingMutation.mutate({ id: bookingId, status: 'confirmed' as const });
  };

  const handleCancel = (bookingId: string) => {
    updateBookingMutation.mutate({ id: bookingId, status: 'cancelled' as const });
  };

  const handleReschedule = (bookingId: string) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      setSelectedBooking(booking);
      setShowRescheduleDrawer(true);
    }
  };

  const handleSaveReschedule = (newDate: string, newTime: string) => {
    if (selectedBooking) {
      updateBookingMutation.mutate({ id: selectedBooking.id, date: newDate, time: newTime });
      setShowRescheduleDrawer(false);
      setSelectedBooking(null);
    }
  };

  // Get current week dates for calendar view
  const getWeekDates = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Start from Monday

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  if (isLoading) return <BookingsPageSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Eyebrow className="mb-3">Customers</Eyebrow>
          <H1>Bookings</H1>
          <Rule className="mt-5" />
        </div>
        <motion.button
          onClick={() => setShowSettingsDrawer(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 text-sm font-medium text-primary ring-1 ring-zinc-300 transition-all hover:bg-white hover:ring-zinc-400"
        >
          <Cog6ToothIcon className="w-4 h-4" />
          Settings
        </motion.button>
      </div>

      {/* Stats Overview — hairline cells, no icon tiles */}
      <HairlineGrid cols="grid-cols-2 lg:grid-cols-4">
        <Stat label="Total bookings" value={stats.total} />
        <Stat label="Confirmed" value={stats.confirmed} />
        <Stat label="Pending" value={stats.pending} />
        <Stat label="Completed" value={stats.completed} />
      </HairlineGrid>

      {/* View Toggle & Filters */}
      <motion.div
        initial="initial"
        animate="animate"
        className="flex flex-wrap items-center justify-between gap-4"
      >
        <div className="flex gap-2">
          {/* View Toggle */}
          <div className="flex bg-paper-deep ring-1 ring-zinc-900/[0.04] rounded-full p-1">
            <motion.button
              onClick={() => setViewMode('list')}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-muted hover:text-primary'
              }`}
            >
              <ListBulletIcon className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </motion.button>
            <motion.button
              onClick={() => setViewMode('calendar')}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-muted hover:text-primary'
              }`}
            >
              <CalendarDaysIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Calendar</span>
            </motion.button>
            <motion.button
              onClick={() => setViewMode('board')}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                viewMode === 'board'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-muted hover:text-primary'
              }`}
            >
              <ViewColumnIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Board</span>
            </motion.button>
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2">
          <FilterDropdown
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'pending', label: 'Pending' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
          <AnimatePresence>
            {filterStatus && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={() => setFilterStatus('')}
                className="px-3 py-2 text-sm text-muted hover:text-primary transition-colors"
              >
                Clear filter
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Bookings Settings Card (when disabled) */}
      {!settings.enabled && (
        features.bookingsEnable ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-50 border border-zinc-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-zinc-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-primary">Online Booking is Disabled</h3>
                <p className="text-sm text-muted mt-1">
                  Enable online booking to allow customers to schedule appointments directly from your website.
                </p>
                <motion.button
                  onClick={() => setShowSettingsDrawer(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-4 px-4 py-2 rounded-full bg-primary text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.99]"
                >
                  Enable Booking
                </motion.button>
              </div>
            </div>
          </motion.div>
        ) : (
          <PlanUpsell
            mark={MARKS.bookings}
            requiredPlan="professional"
            title="Let customers book themselves in"
            description="Online booking puts your calendar on the website, so a job gets scheduled while you're under a sink."
            includes={[
              'Bookings straight from your website',
              'Your hours, days and buffer times',
              'An alert the moment one comes in',
            ]}
          />
        )
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <motion.div
          initial="initial"
          animate="animate"
        >
          {sortedBookings.length === 0 ? (
            // The plan upsell above already explains an empty bookings screen —
            // showing a second illustrated block under it just repeats itself.
            showBookingsUpsell ? null : filterStatus ? (
              <EmptyState
                compact
                title="No bookings found"
                description="No bookings match your filter."
              />
            ) : (
              <EmptyState
                mark={MARKS.bookings}
                eyebrow="Bookings"
                title="No bookings yet"
                description="Appointments customers book on your website land here, newest first."
                hint="Booked slots also sync to the calendar view and trigger an email alert."
              />
            )
          ) : (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="divide-y divide-zinc-100">
                {sortedBookings.map((booking, index) => (
                  <BookingRow
                    key={booking.id}
                    booking={booking}
                    index={index}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    onReschedule={handleReschedule}
                    onViewDetails={() => {
                      setSelectedBookingForDetail(booking);
                      setIsBookingDetailDrawerOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
        >
          {/* Week Header */}
          <div className="grid grid-cols-7 border-b border-zinc-200">
            {weekDates.map((date, i) => {
              const isToday = date.toDateString() === new Date().toDateString();
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = date.getDate();
              return (
                <div
                  key={i}
                  className={`p-3 text-center ${i < 6 ? 'border-r border-zinc-200' : ''}`}
                >
                  <p className="text-xs text-muted uppercase">{dayName}</p>
                  <p className={`text-lg font-semibold mt-1 ${
                    isToday ? 'text-accent' : 'text-primary'
                  }`}>
                    {dayNum}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 min-h-[400px]">
            {weekDates.map((date, i) => {
              const dateStr = date.toISOString().split('T')[0];
              const dayBookings = sortedBookings.filter(b => b.date === dateStr);

              return (
                <div
                  key={i}
                  className={`p-2 ${i < 6 ? 'border-r border-zinc-200' : ''}`}
                >
                  <div className="space-y-1">
                    {dayBookings.map((booking) => {
                      const status = statusConfig[booking.status];
                      return (
                        <motion.div
                          key={booking.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => {
                            setSelectedBookingForDetail(booking);
                            setIsBookingDetailDrawerOpen(true);
                          }}
                          className={`p-2 rounded-lg text-xs cursor-pointer ${status.bgColor}`}
                        >
                          <p className={`font-medium ${status.color} truncate`}>
                            {formatTime(booking.time)}
                          </p>
                          <p className="text-muted truncate mt-0.5">
                            {booking.customerName}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Board View (Kanban by Status) */}
      {viewMode === 'board' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {/* Pending Column */}
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-zinc-500 rounded-full" />
              <h3 className="font-semibold text-primary">Pending</h3>
              <span className="ml-auto text-sm text-muted">
                {bookings.filter(b => b.status === 'pending').length}
              </span>
            </div>
            <div className="space-y-2">
              {sortedBookings.filter(b => b.status === 'pending').map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    setSelectedBookingForDetail(booking);
                    setIsBookingDetailDrawerOpen(true);
                  }}
                  whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  className="p-3 bg-zinc-50 rounded-lg cursor-pointer border border-zinc-200"
                >
                  <p className="font-medium text-primary text-sm truncate">{booking.customerName}</p>
                  <p className="text-xs text-muted mt-1 truncate">{booking.serviceType}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted">{getRelativeDateLabel(booking.date)}</span>
                    <span className="text-xs text-muted">{formatTime(booking.time)}</span>
                  </div>
                </motion.div>
              ))}
              {bookings.filter(b => b.status === 'pending').length === 0 && (
                <p className="text-sm text-muted text-center py-8">No pending bookings</p>
              )}
            </div>
          </div>

          {/* Confirmed Column */}
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <h3 className="font-semibold text-primary">Confirmed</h3>
              <span className="ml-auto text-sm text-muted">
                {bookings.filter(b => b.status === 'confirmed').length}
              </span>
            </div>
            <div className="space-y-2">
              {sortedBookings.filter(b => b.status === 'confirmed').map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    setSelectedBookingForDetail(booking);
                    setIsBookingDetailDrawerOpen(true);
                  }}
                  whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  className="p-3 bg-zinc-50 rounded-lg cursor-pointer border border-zinc-200"
                >
                  <p className="font-medium text-primary text-sm truncate">{booking.customerName}</p>
                  <p className="text-xs text-muted mt-1 truncate">{booking.serviceType}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted">{getRelativeDateLabel(booking.date)}</span>
                    <span className="text-xs text-muted">{formatTime(booking.time)}</span>
                  </div>
                </motion.div>
              ))}
              {bookings.filter(b => b.status === 'confirmed').length === 0 && (
                <p className="text-sm text-muted text-center py-8">No confirmed bookings</p>
              )}
            </div>
          </div>

          {/* Completed Column */}
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <h3 className="font-semibold text-primary">Completed</h3>
              <span className="ml-auto text-sm text-muted">
                {bookings.filter(b => b.status === 'completed').length}
              </span>
            </div>
            <div className="space-y-2">
              {sortedBookings.filter(b => b.status === 'completed').map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    setSelectedBookingForDetail(booking);
                    setIsBookingDetailDrawerOpen(true);
                  }}
                  whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  className="p-3 bg-zinc-50 rounded-lg cursor-pointer border border-zinc-200"
                >
                  <p className="font-medium text-primary text-sm truncate">{booking.customerName}</p>
                  <p className="text-xs text-muted mt-1 truncate">{booking.serviceType}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted">{getRelativeDateLabel(booking.date)}</span>
                    <span className="text-xs text-muted">{formatTime(booking.time)}</span>
                  </div>
                </motion.div>
              ))}
              {bookings.filter(b => b.status === 'completed').length === 0 && (
                <p className="text-sm text-muted text-center py-8">No completed bookings</p>
              )}
            </div>
          </div>

          {/* Cancelled Column */}
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <h3 className="font-semibold text-primary">Cancelled</h3>
              <span className="ml-auto text-sm text-muted">
                {bookings.filter(b => b.status === 'cancelled').length}
              </span>
            </div>
            <div className="space-y-2">
              {sortedBookings.filter(b => b.status === 'cancelled').map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    setSelectedBookingForDetail(booking);
                    setIsBookingDetailDrawerOpen(true);
                  }}
                  whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  className="p-3 bg-zinc-50 rounded-lg cursor-pointer border border-zinc-200 opacity-60"
                >
                  <p className="font-medium text-primary text-sm truncate">{booking.customerName}</p>
                  <p className="text-xs text-muted mt-1 truncate">{booking.serviceType}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted">{getRelativeDateLabel(booking.date)}</span>
                    <span className="text-xs text-muted">{formatTime(booking.time)}</span>
                  </div>
                </motion.div>
              ))}
              {bookings.filter(b => b.status === 'cancelled').length === 0 && (
                <p className="text-sm text-muted text-center py-8">No cancelled bookings</p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Settings Drawer */}
      <BookingSettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        settings={settings}
        onSave={setSettings}
        canEnable={features.bookingsEnable}
      />

      {/* Reschedule Drawer */}
      <RescheduleDrawer
        isOpen={showRescheduleDrawer}
        onClose={() => {
          setShowRescheduleDrawer(false);
          setSelectedBooking(null);
        }}
        booking={selectedBooking}
        onSave={handleSaveReschedule}
      />

      {/* Booking Detail Drawer */}
      <BookingDetailDrawer
        booking={selectedBookingForDetail}
        isOpen={isBookingDetailDrawerOpen}
        onClose={() => {
          setIsBookingDetailDrawerOpen(false);
          setSelectedBookingForDetail(null);
        }}
        onSave={handleBookingUpdate}
        onDelete={handleDeleteBooking}
      />
    </div>
  );
};

// Booking Row Component
interface BookingRowProps {
  booking: Booking;
  index: number;
  onConfirm: (id: string) => void;
  onCancel: (id: string) => void;
  onReschedule: (id: string) => void;
  onViewDetails?: () => void;
}

const BookingRow: React.FC<BookingRowProps> = ({ booking, index, onConfirm, onCancel, onReschedule, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const status = statusConfig[booking.status];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2 }}
    >
      <motion.div
        className="p-4 hover:bg-zinc-100 cursor-pointer transition-colors"
        onClick={() => onViewDetails?.()}
      >
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-blue-700">
              {booking.customerName.charAt(0).toUpperCase()}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-primary truncate">{booking.customerName}</h3>
              <motion.span
                key={booking.status}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}
              >
                {status.label}
              </motion.span>
            </div>
            <p className="text-sm text-muted mt-0.5">{booking.serviceType}</p>
          </div>

          {/* Date/Time */}
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium text-primary">{getRelativeDateLabel(booking.date)}</p>
            <p className="text-sm text-muted">{formatTime(booking.time)} ({booking.duration}min)</p>
          </div>

          {/* Expand Icon */}
          <svg
            className="w-5 h-5 text-zinc-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </div>
      </motion.div>

      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 bg-zinc-50 border-t border-zinc-100">
              <div className="grid md:grid-cols-2 gap-6 pt-4">
                {/* Contact Info */}
                <div>
                  <h4 className="text-sm font-medium text-primary mb-3">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted">
                      <PhoneIcon />
                      <span>{booking.customerPhone}</span>
                    </div>
                    {booking.customerEmail && (
                      <div className="flex items-center gap-2 text-sm text-muted">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                        </svg>
                        <span>{booking.customerEmail}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h4 className="text-sm font-medium text-primary mb-3">Notes</h4>
                  <p className="text-sm text-muted">{booking.notes || 'No notes added.'}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-200">
                {onViewDetails && (
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails();
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-3 py-1.5 bg-primary hover:bg-ink-soft text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    View Details
                  </motion.button>
                )}
                {(booking.status === 'pending' || booking.status === 'confirmed') && (
                  <>
                    {booking.status === 'pending' && (
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          onConfirm(booking.id);
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                      >
                        <CheckIcon />
                        Confirm
                      </motion.button>
                    )}
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReschedule(booking.id);
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-lg text-sm font-medium hover:bg-zinc-200 transition-colors"
                    >
                      <ArrowPathIcon />
                      Reschedule
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancel(booking.id);
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      <XMarkIcon />
                      Cancel
                    </motion.button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Booking Settings Drawer
interface BookingSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  settings: BookingSettings;
  onSave: (settings: BookingSettings) => void;
  canEnable: boolean;
}

const BookingSettingsDrawer: React.FC<BookingSettingsDrawerProps> = ({ isOpen, onClose, settings, onSave, canEnable }) => {
  const [localSettings, setLocalSettings] = useState(settings);

  const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const toggleDay = (day: string) => {
    setLocalSettings(prev => ({
      ...prev,
      availableDays: prev.availableDays.includes(day)
        ? prev.availableDays.filter(d => d !== day)
        : [...prev.availableDays, day],
    }));
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Bookings"
      title="Booking settings"
      subtitle="When customers can book you, and how much room to leave between jobs."
      footer={
        <div className="flex gap-2">
          <Pill tone="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Pill>
          <Pill className="flex-1" onClick={handleSave}>
            Save changes
          </Pill>
        </div>
      }
    >
      <div className="space-y-8">
        {/* The switch that decides whether any of the rest matters. */}
        <section className="rounded-xl border border-zinc-200 bg-paper-tint p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-primary">Online booking</p>
              <p className="mt-1 text-[14px] text-muted">
                {canEnable
                  ? 'Adds a booking form to your website so customers can pick a slot themselves.'
                  : 'Included from the Managed plan up.'}
              </p>
            </div>
            {canEnable ? (
              <Toggle
                checked={localSettings.enabled}
                onChange={() => setLocalSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
              />
            ) : (
              <Pill to="/dashboard/account/billing" size="sm" onClick={onClose}>
                Upgrade
              </Pill>
            )}
          </div>
        </section>

        <section className="space-y-5">
          <Eyebrow>Availability</Eyebrow>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Opens" htmlFor="booking-start">
              <input
                id="booking-start"
                type="time"
                value={localSettings.availableHours.start}
                onChange={(e) => setLocalSettings(prev => ({
                  ...prev,
                  availableHours: { ...prev.availableHours, start: e.target.value }
                }))}
                className="input-field"
              />
            </Field>
            <Field label="Closes" htmlFor="booking-end">
              <input
                id="booking-end"
                type="time"
                value={localSettings.availableHours.end}
                onChange={(e) => setLocalSettings(prev => ({
                  ...prev,
                  availableHours: { ...prev.availableHours, end: e.target.value }
                }))}
                className="input-field"
              />
            </Field>
          </div>

          <div className="space-y-2">
            <span className="block font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
              Days you work
            </span>
            <div className="flex flex-wrap gap-2">
              {allDays.map((day) => {
                const on = localSettings.availableDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    aria-pressed={on}
                    className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-eyebrow transition-colors ${
                      on
                        ? 'bg-primary text-white'
                        : 'bg-white text-zinc-500 ring-1 ring-zinc-300 hover:ring-zinc-400'
                    }`}
                  >
                    {day.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section>
          <Field
            label="Gap between jobs"
            htmlFor="booking-buffer"
            hint="Travel and clean-up time held back after every appointment."
          >
            <select
              id="booking-buffer"
              value={localSettings.bufferTime}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, bufferTime: parseInt(e.target.value) }))}
              className="input-field"
            >
              <option value={0}>No gap</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
            </select>
          </Field>
        </section>
      </div>
    </Drawer>
  );
};

// Reschedule Drawer
interface RescheduleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
  onSave: (newDate: string, newTime: string) => void;
}

const RescheduleDrawer: React.FC<RescheduleDrawerProps> = ({ isOpen, onClose, booking, onSave }) => {
  const [newDate, setNewDate] = useState(booking?.date || '');
  const [newTime, setNewTime] = useState(booking?.time || '');

  React.useEffect(() => {
    if (booking) {
      setNewDate(booking.date);
      setNewTime(booking.time);
    }
  }, [booking]);

  const handleSave = () => {
    if (newDate && newTime) {
      onSave(newDate, newTime);
    }
  };

  if (!booking) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Reschedule"
      title={booking.customerName}
      subtitle={booking.serviceType}
      footer={
        <div className="flex gap-2">
          <Pill tone="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Pill>
          <Pill className="flex-1" onClick={handleSave} disabled={!newDate || !newTime}>
            Move appointment
          </Pill>
        </div>
      }
    >
      <div className="space-y-8">
        {/* What you're moving it from — the thing you'd otherwise have to remember. */}
        <section>
          <Eyebrow className="mb-3">Currently booked</Eyebrow>
          <div className="rounded-xl border border-zinc-200 bg-paper-tint p-4">
            <p className="font-display text-lg font-bold tracking-tight text-primary">
              {formatDate(booking.date)}
            </p>
            <p className="mt-1 font-mono text-[13px] text-zinc-600">
              {formatTime(booking.time)} · {booking.duration} min
            </p>
          </div>
        </section>

        <section className="space-y-5">
          <Eyebrow>Move it to</Eyebrow>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date" htmlFor="reschedule-date">
              <input
                id="reschedule-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="input-field"
              />
            </Field>
            <Field label="Time" htmlFor="reschedule-time">
              <input
                id="reschedule-time"
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className="input-field"
              />
            </Field>
          </div>
          <p className="text-[13px] text-muted">
            The customer isn't told automatically — give them a call once you've moved it.
          </p>
        </section>
      </div>
    </Drawer>
  );
};

// Filter Dropdown Component
interface FilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  const isFiltered = value !== '';

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border transition-all
          ${isFiltered
            ? 'bg-accent/10 border-accent/30 text-accent'
            : 'bg-white border-zinc-200 text-primary hover:bg-zinc-100'
          }
        `}
      >
        {selectedOption?.label}
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </motion.svg>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              variants={dropdownVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg z-20 min-w-[160px] overflow-hidden"
            >
              {options.map((option, index) => (
                <motion.button
                  key={option.value}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                    ${option.value === value
                      ? 'bg-accent/10 text-accent font-medium'
                      : 'text-primary hover:bg-zinc-100'
                    }
                  `}
                >
                  {option.label}
                </motion.button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingsPage;
